import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import wittmannLogo from "@/assets/wittmann-logo.webp";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const equipmentLinks = [
    { path: "/echipamente/masini-de-injectie", label: t("nav.injectionMachines") },
    { path: "/echipamente/roboti", label: t("nav.robots") },
    { path: "/echipamente/auxiliare", label: t("nav.auxiliaries") },
    { path: "/echipamente/sisteme-centralizate", label: t("nav.centralSystems") },
  ];

  const closeMobile = () => {
    setMobileOpen(false);
    setEquipmentOpen(false);
  };

  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50">
      <div className="section-container flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-bold text-lg tracking-tight text-primary" onClick={closeMobile}>
          <img src={wittmannLogo} alt="WITTMANN" className="h-8 w-auto" />
          <span>ROMANIA</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          <NavItem to="/" active={isActive("/")}>{t("nav.home")}</NavItem>
          <NavItem to="/despre-noi" active={isActive("/despre-noi")}>{t("nav.about")}</NavItem>
          <Link
            to="/promotii"
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors ${
              isActive("/promotii")
                ? "bg-primary text-primary-foreground"
                : "text-primary hover:bg-primary/10"
            }`}
          >
            {t("nav.promotions")}
          </Link>

          {/* Equipment Dropdown */}
          <div className="relative group">
            <Link
              to="/echipamente"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                location.pathname.startsWith("/echipamente")
                  ? "text-primary bg-primary/10"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted"
              }`}
            >
              {t("nav.equipment")}
              <ChevronDown className="w-3.5 h-3.5" />
            </Link>
            <div className="absolute top-full left-0 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="bg-card border border-border rounded-lg shadow-lg py-2 min-w-[220px]">
                {equipmentLinks.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block px-4 py-2.5 text-sm transition-colors ${
                      isActive(item.path)
                        ? "text-primary bg-primary/5"
                        : "text-foreground/70 hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <NavItem to="/contact" active={isActive("/contact")}>{t("nav.contact")}</NavItem>

          {/* Language Switcher */}
          <div className="ml-4 flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setLang("ro")}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                lang === "ro" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              RO
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              EN
            </button>
          </div>
        </div>

        {/* Mobile toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setLang("ro")}
              className={`px-2.5 py-1 text-xs font-semibold transition-colors ${
                lang === "ro" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              RO
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 text-xs font-semibold transition-colors ${
                lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              EN
            </button>
          </div>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-foreground">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden overflow-hidden border-t border-border bg-card"
          >
            <div className="section-container py-4 flex flex-col gap-1">
              <MobileNavItem to="/" onClick={closeMobile} active={isActive("/")}>{t("nav.home")}</MobileNavItem>
              <MobileNavItem to="/despre-noi" onClick={closeMobile} active={isActive("/despre-noi")}>{t("nav.about")}</MobileNavItem>
              <Link
                to="/promotii"
                onClick={closeMobile}
                className={`block px-4 py-3 text-sm font-bold rounded-md transition-colors ${
                  isActive("/promotii")
                    ? "bg-primary text-primary-foreground"
                    : "text-primary hover:bg-primary/10"
                }`}
              >
                {t("nav.promotions")}
              </Link>

              <button
                onClick={() => setEquipmentOpen(!equipmentOpen)}
                className="flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground/70 rounded-md hover:bg-muted"
              >
                {t("nav.equipment")}
                <ChevronDown className={`w-4 h-4 transition-transform ${equipmentOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {equipmentOpen && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    className="overflow-hidden pl-4"
                  >
                    <MobileNavItem to="/echipamente" onClick={closeMobile} active={isActive("/echipamente")}>
                      {t("nav.equipment")} — {lang === "ro" ? "Toate" : "All"}
                    </MobileNavItem>
                    {equipmentLinks.map((item) => (
                      <MobileNavItem key={item.path} to={item.path} onClick={closeMobile} active={isActive(item.path)}>
                        {item.label}
                      </MobileNavItem>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              <MobileNavItem to="/contact" onClick={closeMobile} active={isActive("/contact")}>{t("nav.contact")}</MobileNavItem>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

function NavItem({ to, active, children }: { to: string; active: boolean; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
        active ? "text-primary bg-primary/10" : "text-foreground/70 hover:text-foreground hover:bg-muted"
      }`}
    >
      {children}
    </Link>
  );
}

function MobileNavItem({ to, onClick, active, children }: { to: string; onClick: () => void; active: boolean; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-4 py-3 text-sm font-medium rounded-md transition-colors ${
        active ? "text-primary bg-primary/5" : "text-foreground/70 hover:bg-muted"
      }`}
    >
      {children}
    </Link>
  );
}
